import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  Row,
  Col,
  Card,
  Container,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useCreateBlogMutation } from "../../../redux/api/BlogsApi/blogsApi";
import { useGetBlogCategoriesQuery } from "../../../redux/api/BlogCategoriesApi/blogCategoriesApi";
import { FaArrowLeft, FaSave, FaImage, FaAsterisk } from "react-icons/fa";
import { message } from "antd";
import LoaderSpinner from "../../../Components/LoaderSpinner";

const AddBlog = () => {
  const navigate = useNavigate();
  const [createBlog, { isLoading }] = useCreateBlogMutation();
  const { data: categories, isLoading: isCatsLoading } =
    useGetBlogCategoriesQuery();

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    category_id: "",
    summary: "",
    content: "",
    author: "Ideal Corp",
    image: null,
    top_read: false,
    status: true,
    meta_title: "",
    meta_keyword: "",
    meta_description: "",
  });

  const [imagePreview, setImagePreview] = useState(null);

  // Auto-slug
  useEffect(() => {
    if (formData.title) {
      setFormData((prev) => ({
        ...prev,
        slug: prev.title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, ""),
      }));
    }
  }, [formData.title]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "file") {
      const file = files[0];
      setFormData((prev) => ({ ...prev, [name]: file }));
      if (file) {
        setImagePreview(URL.createObjectURL(file));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.content) {
      return message.error("Blog content is required");
    }

    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === "image" && formData[key]) {
        data.append("image", formData[key]);
      } else {
        data.append(key, formData[key]);
      }
    });

    try {
      await createBlog(data).unwrap();
      message.success("Blog post created successfully!");
      navigate("/admin/blogs");
    } catch (error) {
      message.error(error.data?.error || "Failed to create blog post");
    }
  };

  if (isCatsLoading)
    return (
      <div className="d-flex justify-content-center py-5">
        <LoaderSpinner />
      </div>
    );

  return (
    <Container className="py-4 fade-in">
      <div className="d-flex align-items-center gap-3 mb-4">
        <Button
          variant="light"
          onClick={() => navigate("/admin/blogs")}
          className="rounded-circle shadow-sm p-2"
        >
          <FaArrowLeft />
        </Button>
        <h2 className="fw-bold text-dark mb-0">Create New Blog Post</h2>
      </div>

      <Form onSubmit={handleSubmit}>
        <Row className="g-4">
          <Col lg={8}>
            <Card className="border-0 shadow-sm rounded-4 p-4 mb-4">
              <h5 className="fw-bold mb-4">Content Details</h5>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  Blog Title <FaAsterisk className="text-danger" size={8} />
                </Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter a compelling title..."
                  required
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">URL Slug</Form.Label>
                    <Form.Control
                      type="text"
                      name="slug"
                      value={formData.slug}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Category</Form.Label>
                    <Form.Select
                      name="category_id"
                      value={formData.category_id}
                      onChange={handleChange}
                    >
                      <option value="">Select Category</option>
                      {categories
                        ?.filter((c) => c.status)
                        .map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Short Summary</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="summary"
                  value={formData.summary}
                  onChange={handleChange}
                  placeholder="A brief overview for listings..."
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                  Full Content (Rich Text){" "}
                  <FaAsterisk className="text-danger" size={8} />
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={15}
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  className="blog-editor"
                  placeholder="Start writing your blog content here..."
                  required
                />
                <Form.Text className="text-muted">
                  You can use HTML tags for formatting (e.g., &lt;h2&gt;,
                  &lt;p&gt;, &lt;ul&gt;).
                </Form.Text>
              </Form.Group>
            </Card>

            <Card className="border-0 shadow-sm rounded-4 p-4 mb-4">
              <h5 className="fw-bold mb-4">SEO Details (Optional)</h5>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Meta Title</Form.Label>
                <Form.Control
                  type="text"
                  name="meta_title"
                  value={formData.meta_title}
                  onChange={handleChange}
                  placeholder="Enter SEO meta title..."
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Meta Keywords</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="meta_keyword"
                  value={formData.meta_keyword}
                  onChange={handleChange}
                  placeholder="keyword1, keyword2, keyword3..."
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Meta Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="meta_description"
                  value={formData.meta_description}
                  onChange={handleChange}
                  placeholder="Enter SEO meta description..."
                />
              </Form.Group>
            </Card>
          </Col>

          <Col lg={4}>
            <Card
              className="border-0 shadow-sm rounded-4 p-4 mb-4 sticky-top"
              style={{ top: "20px" }}
            >
              <h5 className="fw-bold mb-4">Publish & Media</h5>

              <Form.Group className="mb-4">
                <Form.Label className="fw-bold">Featured Image</Form.Label>
                <div className="text-center mb-3">
                  <div
                    className="border rounded-4 d-flex align-items-center justify-content-center bg-light overflow-hidden"
                    style={{ height: "200px", cursor: "pointer" }}
                    onClick={() =>
                      document.getElementById("blog-image").click()
                    }
                  >
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-100 h-100 object-fit-cover"
                      />
                    ) : (
                      <div className="text-muted">
                        <FaImage size={40} className="mb-2 d-block mx-auto" />
                        <span>Click to upload image</span>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    id="blog-image"
                    name="image"
                    onChange={handleChange}
                    hidden
                    accept="image/*"
                  />
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Author Name</Form.Label>
                <Form.Control
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Check
                  type="switch"
                  id="top-read-switch"
                  label="Mark as Top Read"
                  name="top_read"
                  checked={formData.top_read}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Check
                  type="switch"
                  id="status-switch"
                  label="Published"
                  name="status"
                  checked={formData.status}
                  onChange={handleChange}
                  className="fw-bold text-success"
                />
              </Form.Group>

              <div className="d-grid gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="rounded-pill shadow-sm"
                  disabled={isLoading}
                >
                  <FaSave className="me-2" />
                  {isLoading ? "creating..." : "Publish Blog Post"}
                </Button>
                <Button
                  variant="light"
                  className="rounded-pill"
                  onClick={() => navigate("/admin/blogs")}
                >
                  Cancel
                </Button>
              </div>
            </Card>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default AddBlog;
